import type { Automation, ClientMessage, CustomerSkill, DataListing, FeaturedSkill, IntegrationStatus, ServerEvent } from '@harness/shared/protocol';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  type ChatSummary,
  deleteDataFile,
  disconnectIntegration,
  fetchAutomations,
  fetchChats,
  fetchData,
  fetchEvents,
  fetchFeaturedSkills,
  fetchIntegrations,
  fetchMe,
  fetchSkills,
  type Me,
  socketUrl,
} from './api';
import { Automations } from './components/Automations';
import { Brand } from './components/Brand';
import { ChatView } from './components/ChatView';
import { Data } from './components/Data';
import { Integrations } from './components/Integrations';
import { PageHeader } from './components/PageHeader';
import { Placeholder } from './components/Placeholder';
import { ProjectSelect } from './components/ProjectSelect';
import { Sidebar } from './components/Sidebar';
import { Skills } from './components/Skills';
import { automationRouteFromPath, projectUrl, type Section, sectionFromPath } from './sections';
import { type ChatSocket, openChatSocket, type SocketLike } from './socket';
import { initialState, reduce } from './state';
import styles from './styles/app.module.css';
import { sectionHeader, sectionName, TEXT } from './texts';

const LAST_CHAT_KEY = 'harness.lastChat';
const LAST_PROJECT_KEY = 'harness.lastProject';

/** sessionStorage can be missing or throw (private mode); losing the last chat is harmless. */
function rememberChat(projectId: string, chatId: string | null): void {
  try {
    if (chatId === null) sessionStorage.removeItem(`${LAST_CHAT_KEY}.${projectId}`);
    else sessionStorage.setItem(`${LAST_CHAT_KEY}.${projectId}`, chatId);
  } catch {
    // ignore
  }
}

function lastChat(projectId: string): string | null {
  try {
    return sessionStorage.getItem(`${LAST_CHAT_KEY}.${projectId}`);
  } catch {
    return null;
  }
}

/** localStorage can be missing or throw; then `/` simply opens the first project. */
function rememberProject(projectId: string): void {
  try {
    localStorage.setItem(LAST_PROJECT_KEY, projectId);
  } catch {
    // ignore
  }
}

function lastProject(): string | null {
  try {
    return localStorage.getItem(LAST_PROJECT_KEY);
  } catch {
    return null;
  }
}

/** A customer lives at /<id> and its areas below it; the API stays under /p/<id>/. */
function projectFromPath(pathname: string): string | null {
  const segment = pathname.split('/')[1] ?? '';
  if (segment === '') return null;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export interface AppProps {
  /** Tests pass a fake WebSocket. */
  createSocket?: (url: string) => SocketLike;
}

interface ProjectProps extends AppProps {
  me: Me;
  projectId: string;
  onProject: (id: string) => void;
  section: Section;
  onSection: (section: Section) => void;
  /** The address, for the pages within an area. */
  path: string;
  go: (url: string) => void;
}

function Project({ me, projectId, onProject, section, onSection, path, go, createSocket }: ProjectProps): React.JSX.Element {
  const [state, dispatch] = useReducer(reduce, initialState);
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [integrations, setIntegrations] = useState<IntegrationStatus[] | null | 'failed'>(null);
  const [featured, setFeatured] = useState<FeaturedSkill[]>([]);
  const [automations, setAutomations] = useState<Automation[] | null | 'failed'>(null);
  const [data, setData] = useState<DataListing | null | 'failed'>(null);
  const [skills, setSkills] = useState<CustomerSkill[] | null | 'failed'>(null);
  // The socket handler outlives section changes; it reloads the customer data and skills only while the page shows them.
  const showsData = useRef(false);
  showsData.current = section === 'data';
  const showsSkills = useRef(false);
  showsSkills.current = section === 'skills';
  const socket = useRef<ChatSocket | null>(null);
  const chatId = useRef<string | null>(null);
  chatId.current = state.chatId;

  const refreshChats = useCallback((): void => {
    fetchChats(projectId).then(setChats, () => undefined);
  }, [projectId]);

  // Read again after every answer: a new standard skills rollout goes live without a reload.
  const refreshFeatured = useCallback((): void => {
    fetchFeaturedSkills(projectId).then(setFeatured, () => undefined);
  }, [projectId]);

  const refreshIntegrations = useCallback((): void => {
    fetchIntegrations(projectId).then(setIntegrations, () => setIntegrations((current) => (Array.isArray(current) ? current : 'failed')));
  }, [projectId]);

  useEffect(() => {
    if (section === 'integrations') refreshIntegrations();
  }, [section, refreshIntegrations]);

  const refreshData = useCallback((): void => {
    fetchData(projectId).then(setData, () => setData((current) => (current !== null && current !== 'failed' ? current : 'failed')));
  }, [projectId]);

  useEffect(() => {
    if (section === 'data') refreshData();
  }, [section, refreshData]);

  const refreshSkills = useCallback((): void => {
    fetchSkills(projectId).then(setSkills, () => setSkills((current) => (Array.isArray(current) ? current : 'failed')));
  }, [projectId]);

  useEffect(() => {
    if (section === 'skills') refreshSkills();
  }, [section, refreshSkills]);

  // Loaded on every page: the navigation shows how many automations need the consultant's attention.
  const refreshAutomations = useCallback((): void => {
    fetchAutomations(projectId).then(setAutomations, () => setAutomations((current) => (Array.isArray(current) ? current : 'failed')));
  }, [projectId]);

  const send = useCallback((message: ClientMessage): boolean => socket.current?.send(message) ?? false, []);

  const openChat = useCallback(
    async (id: string, title: string): Promise<void> => {
      // Subscribe first: events stored after this point arrive live, earlier ones come with the history.
      send({ type: 'subscribe', chatId: id });
      const events = await fetchEvents(projectId, id).catch(() => null);
      if (events === null) {
        rememberChat(projectId, null);
        return;
      }
      dispatch({ type: 'history', chatId: id, title, events });
      rememberChat(projectId, id);
    },
    [projectId, send],
  );

  useEffect(() => {
    refreshChats();
    refreshFeatured();
    refreshAutomations();
    let everConnected = false;
    const current = openChatSocket({
      url: socketUrl(projectId, window.location),
      ...(createSocket === undefined ? {} : { create: createSocket }),
      onEvent: (event: ServerEvent) => {
        dispatch({ type: 'server', event });
        if (event.type === 'integrations') setIntegrations(event.integrations);
        if (event.type === 'automations') refreshAutomations();
        if (event.type === 'data' && showsData.current) refreshData();
        if (event.type === 'skills' && showsSkills.current) refreshSkills();
        if (event.type === 'chat') rememberChat(projectId, event.chatId);
        if (event.type === 'chat' || event.type === 'done') refreshChats();
        // A customer skill can be a chip, so its changes count like a new rollout of the standard skills.
        if (event.type === 'done' || event.type === 'skills') refreshFeatured();
      },
      onConnection: (connected) => {
        dispatch({ type: 'connection', connected });
        if (!connected) return;
        // Changes while the socket was down were not announced.
        if (everConnected) refreshAutomations();
        // After a reconnect reload the open chat; on the first connect restore the last one of this tab.
        const reopen = everConnected ? chatId.current : lastChat(projectId);
        everConnected = true;
        if (reopen !== null) void openChat(reopen, '');
      },
    });
    socket.current = current;
    return () => {
      current.close();
      socket.current = null;
    };
  }, [projectId, createSocket, openChat, refreshChats, refreshFeatured, refreshAutomations, refreshData, refreshSkills]);

  const title = chats.find((chat) => chat.id === state.chatId)?.title;
  const view = title === undefined || state.title === title ? state : { ...state, title };
  const projectName = me.projects.find((candidate) => candidate.id === projectId)?.name ?? projectId;
  const header =
    section === 'chat'
      ? { eyebrow: sectionName.chat, title: view.title === null || view.title === '' ? TEXT.newChat : view.title, subtitle: TEXT.chatSubtitle(projectName) }
      : sectionHeader[section];
  const showChat = (): void => {
    if (section !== 'chat') onSection('chat');
  };

  return (
    <div className={styles.shell}>
      <Sidebar
        me={me}
        projectId={projectId}
        onProject={onProject}
        section={section}
        onSection={onSection}
        chats={chats}
        activeChatId={state.chatId}
        onOpen={(id) => {
          showChat();
          void openChat(id, chats.find((chat) => chat.id === id)?.title ?? '');
        }}
        onNew={() => {
          showChat();
          dispatch({ type: 'new-chat' });
          rememberChat(projectId, null);
        }}
        badges={{ automations: Array.isArray(automations) ? automations.filter((automation) => automation.attention).length : 0 }}
      />
      <div className={styles.content}>
        {/* The automations, data and skills areas head their pages themselves, with their own buttons. */}
        {section !== 'automations' && section !== 'data' && section !== 'skills' && <PageHeader {...header} />}
        {section === 'chat' ? (
          <ChatView state={view} projectId={projectId} ownName={me.name} featured={featured} send={send} onSent={() => dispatch({ type: 'sent-prompt' })} />
        ) : section === 'integrations' ? (
          <Integrations
            projectId={projectId}
            integrations={integrations}
            onDisconnect={async (id) => {
              const ok = await disconnectIntegration(projectId, id);
              // The socket reports the change too; reloading covers a dropped connection.
              if (ok) refreshIntegrations();
              return ok;
            }}
          />
        ) : section === 'data' ? (
          <Data
            projectId={projectId}
            listing={data}
            onChanged={refreshData}
            onDelete={async (name) => {
              const ok = await deleteDataFile(projectId, name);
              if (ok) refreshData();
              return ok;
            }}
          />
        ) : section === 'skills' ? (
          <Skills projectId={projectId} skills={skills} onChanged={refreshSkills} />
        ) : section === 'automations' ? (
          <Automations
            projectId={projectId}
            ownName={me.name}
            automations={automations}
            route={automationRouteFromPath(path)}
            onNavigate={go}
            onChanged={refreshAutomations}
          />
        ) : (
          <Placeholder />
        )}
      </div>
    </div>
  );
}

export function App({ createSocket }: AppProps = {}): React.JSX.Element {
  const [me, setMe] = useState<Me | null | 'loading' | 'failed'>('loading');
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    fetchMe().then(setMe, () => setMe('failed'));
    const onPop = (): void => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = useCallback((url: string, replace = false): void => {
    if (replace) window.history.replaceState(null, '', url);
    else window.history.pushState(null, '', url);
    setPath(window.location.pathname);
  }, []);

  const wanted = projectFromPath(path);
  const projects = useMemo(() => (me !== null && typeof me === 'object' ? me.projects : []), [me]);
  const project = projects.find((candidate) => candidate.id === wanted);

  // `/` opens the last used project of this browser, or the first one the person may see.
  useEffect(() => {
    if (wanted !== null || projects.length === 0) return;
    const remembered = lastProject();
    go(projectUrl(projects.some((candidate) => candidate.id === remembered) && remembered !== null ? remembered : (projects[0]?.id ?? '')), true);
  }, [wanted, projects, go]);

  useEffect(() => {
    if (project !== undefined) rememberProject(project.id);
    document.title = project === undefined ? TEXT.appTitle : `${project.name} – ${TEXT.appTitle}`;
  }, [project]);

  if (me === 'loading') return <div className={styles.page}>{TEXT.loading}</div>;
  if (me === 'failed') return <div className={styles.page}>{TEXT.loadFailed}</div>;
  if (me === null)
    return (
      <div className={styles.page}>
        <div className={styles.signIn}>
          <Brand />
          <p>{TEXT.signIn}</p>
          <a className={styles.signInButton} href={wanted === null ? '/auth/login' : `/auth/login?next=${encodeURIComponent(projectUrl(wanted))}`}>
            {TEXT.signInButton}
          </a>
        </div>
      </div>
    );
  if (projects.length === 0) return <div className={styles.page}>{TEXT.noProjects}</div>;
  if (wanted === null) return <div className={styles.page}>{TEXT.loading}</div>;
  if (project === undefined)
    return (
      <div className={styles.page}>
        <div className={styles.signIn}>
          <p>{TEXT.noAccess}</p>
          <ProjectSelect projects={projects} value="" onChange={(id) => go(projectUrl(id))} />
        </div>
      </div>
    );
  return (
    <Project
      key={project.id}
      me={me}
      projectId={project.id}
      onProject={(id) => go(projectUrl(id))}
      section={sectionFromPath(path)}
      onSection={(section) => go(projectUrl(project.id, section))}
      path={path}
      go={go}
      {...(createSocket === undefined ? {} : { createSocket })}
    />
  );
}
